import PortfolioComponent from "./portfolioComponent";
import PortfolioComposite from "./portfolioComposite";
import { Expose, Type } from "class-transformer";
import RebalancePlan from "./rebalancePlan";

export default class Portfolio {
  /** The portfolio composite that belongs to this portfolio  */
  @Type(() => PortfolioComposite)
  @Expose() portfolioComposite?: PortfolioComposite;

  /**
   * @param id GUID
   * @param userId ID of the user to which this portfolio belongs to
   * @param name 
   * @param description 
   * @param portfolioComposite 
   */
  constructor(
    public id: string | undefined,
    public userId: string,
    public name: string,
    public description: string,
    portfolioComposite: PortfolioComposite | undefined
  ) {
    this.portfolioComposite = portfolioComposite;
  }

  /**
   * Recalculates notional and percentage ideal values.
   */
  recalculateValues(valueToInvest: number = 0): void {
    this.portfolioComposite?.setTotalPortfoliolNotional(this.portfolioComposite.notional());
    this.portfolioComposite?.setIdealNotional(this.portfolioComposite.totalPortfolioNotional + valueToInvest);
    this.portfolioComposite?.setDynamicWeight();
  }

  /**
   * Removes a component from the portfolio.
   * @param componentId The id of the component to be removed.
   * @returns True if a component was removed, false otherwise.
   */
  removeComponent(componentId: string) {
    return this.portfolioComposite?.removeComponent(componentId) || false;
  }

  /**
   * Updates a composite in the portfolio.
   * @param composite The composite to be updated.
   * @returns True if a composite was updated, false otherwise.
   */
  updateComposite(composite: PortfolioComposite): boolean {
    return this.portfolioComposite?.updateComposite(composite) || false;
  }

  /**
   * Updates a component.
   * @param component The component to be updated.
   */
  updateComponent(component: PortfolioComponent): boolean {
    return this.portfolioComposite?.updateComponent(component) || false;
  };

  /**
   * Add a component to the portfolio. Note that it will only be added if there is any composite whose id matches the component parent id.
   * @param component The component to be added.
   * @returns True if a component was added, false otherwise.
   */
  addComponent(component: PortfolioComponent): boolean {
    return this.portfolioComposite?.addComponent(component) || false;
  };

  /**
   * Creates a rebalance plan to make all the nested positions to become as near as possible to their ideal values.
   * @param newInvestment The notional value of new money to be invested, defaults to 0.
   * @param allowFractionQuantity Informs if fractionary quantities are allowed. e.g. brazilian stocks in generall allow 100 quantity buy/sell, but fractionary allows for 1.
   */
  createRebalancePlan(newInvestment: number = 0, allowFractionQuantity: boolean = false): RebalancePlan {
    const plan: RebalancePlan = new RebalancePlan(undefined, this.id ?? '');

    if (this.portfolioComposite) {
      plan.rebalanceItems.push(...this.portfolioComposite.calculateRebalanceItems(newInvestment, allowFractionQuantity));
    }

    return plan;
  }
}